import { isAxiosError } from "axios";
import React, { useMemo, useState } from "react";
import { toastr } from "react-redux-toastr";
import { request } from "../../../utils/request";

/**
 * @param {{
 *  region: import("../SearchLocationsPage").Region,
 *  locations: import("../SearchLocationsPage").Location[],
 *  params: {
 *      mode: string,
 *      searchTerm?: string,
 *      daysOutFromDelivery?: number,
 *      daysOutFromCollection?: number,
 *      customerTypes?: string,
 *      isBulk?: boolean,
 *      isTins?: boolean,
 *      isBib?: boolean,
 *      bulkCapacity?: number,
 *      minTins?: number,
 *      minBib?: number,
 *      isUrgent?: boolean,
 *      hasTankPickups?: boolean,
 *      hasTankDropOffs?: boolean,
 *      regionIds: number[]
 *  },
 *  newRun: import("../SearchLocationsPage").NewRun,
 *  filterTinBulkNew: string,
 *  onLoadLocations: (locations: import("../SearchLocationsPage").Location[]) => void
 *  onFillTruck: (evnt, locations: import("../SearchLocationsPage").Location[], level:number) => void,
 *  renderLocation: (location: import("../SearchLocationsPage").Location, isSelected: boolean, regionName: string) => JSX.Element,
 *  allowFetchLocations: boolean
 * }} props
 * @returns
 */
export const RegionView = ({
  newRun,
  filterTinBulkNew,
  region,
  locations = [],
  params,
  onFillTruck,
  onLoadLocations = (locations) => {
    console.log(locations);
  },
  renderLocation = () => <React.Fragment></React.Fragment>,
  allowFetchLocations = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);

  function toggleCardOpen() {
    setCardOpen((s) => !s);
  }

  /**
   *
   * @param {Location[]} locations
   * @returns
   */
  const filteredLocations = useMemo(() => {
    return locations.filter(
      (x) =>
        (x.isTinLocation && filterTinBulkNew.includes("tin")) ||
        (!x.isTinLocation && filterTinBulkNew.includes("bulk")) ||
        ((x.daysSinceLastPickup == undefined ||
          x.daysSinceLastPickup == null) &&
          filterTinBulkNew.includes("new"))
    );
  }, [locations, filterTinBulkNew]);

  const urgentForRegion = useMemo(() => {
    let reduceVal = locations.reduce((a, b, i, o) => {
      if (newRun.locations.findIndex((y) => y.id == o[i].id) >= 0) {
        return a + 0;
      } else {
        return o[i].isUrgentJob ? a + 1 : a + 0;
      }
    }, 0);
    return reduceVal;
  }, [locations, newRun]);

  const currentForRegion = useMemo(() => {
    let reduceVal = filteredLocations.reduce((a, b, i, o) => {
      if (newRun.locations.findIndex((y) => y.id == o[i].id) >= 0) {
        return a + 0;
      } else {
        return a + 1;
      }
    }, 0);
    return reduceVal;
  }, [filteredLocations, newRun]);

  const renderedLocations = useMemo(() => {
    const remainingLocations = filteredLocations.map((x) => {
      if (newRun.locations.findIndex((y) => y.id == x.id) >= 0) {
        return null;
      } else {
        return renderLocation(x, false, region.name);
      }
    });
    return remainingLocations.length > 0 ? (
      remainingLocations
    ) : (
      <div className="row justify-content-center">
        <div className="col-auto">No locations in region</div>
      </div>
    );
  }, [filteredLocations, newRun]);

  async function fetchLocations() {
    setLoading(true);
    try {
      /**
       * @type {import("axios").AxiosResponse<Location[]>}
       */
      const response = await request.get("/api/locations", {
        params: {
          regionId: region.id,
          ...params,
        },
      });
      onLoadLocations(response.data);
    } catch (error) {
      if (isAxiosError(error)) {
        console.log(error);
      }
      toastr.error("Error", "Could no retrieve locations");
    } finally {
      setLoading(false);
    }
  }

  return (
    <React.Fragment>
      <div className="card">
        <div
          className="card-header"
          role="tab"
          id={`region_${region.id}_heading`}
        >
          <h5 className="mb-0">
            <a
              data-toggle="collapse"
              data-parent={`#regions`}
              href={`#region_${region.id}_locations`}
              aria-expanded={cardOpen}
              aria-controls={`region_${region.id}_locations`}
              onClick={(e) => {
                e.preventDefault();
                toggleCardOpen();
                if (
                  !cardOpen &&
                  locations.length === 0 &&
                  allowFetchLocations
                ) {
                  fetchLocations();
                }
              }}
            >
              {region.name}
            </a>
            &nbsp;
            {locations.length > 0 && (
              <span className="text-muted small">
                ({currentForRegion} locations, {urgentForRegion} urgent,{" "}
                {region.countExceedCapacity} over capacity)
              </span>
            )}
            {newRun.run.truckId > 0 && filteredLocations.length > 0 && (
              <span className="pull-right">
                Fill to:&nbsp;
                <a href="#" onClick={(e) => onFillTruck(e, locations, 50)}>
                  &nbsp;50%&nbsp;
                </a>
                <a href="#" onClick={(e) => onFillTruck(e, locations, 75)}>
                  &nbsp;75%&nbsp;
                </a>
                <a href="#" onClick={(e) => onFillTruck(e, locations, 100)}>
                  &nbsp;100%&nbsp;
                </a>
                <a href="#" onClick={(e) => onFillTruck(e, locations, 125)}>
                  &nbsp;125%&nbsp;
                </a>
              </span>
            )}
            {newRun.run.truckId <= 0 && (
              <span className="text-danger pull-right">
                Select truck to fill
              </span>
            )}
          </h5>
        </div>
        <div
          id={`region_${region.id}_locations`}
          className="collapse hide"
          role="tabpanel"
          aria-labelledby={`region_${region.id}_heading`}
        >
          <div className="card-block">
            {loading ? (
              <div className="row justify-content-center">
                <div className="col-auto">
                  Loading... <i className="fa fa-cog fa-spin" />
                </div>
              </div>
            ) : (
              renderedLocations
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default RegionView;
